import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Sankey } from 'recharts';
import '../../App.css';

const ArtemisGovernance = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Data for visualizations
  const complianceScore = [
    { category: 'Code Lineage', score: 97, fullScore: 100 },
    { category: 'Evolution Tracking', score: 95, fullScore: 100 },
    { category: 'Performance Audit', score: 93, fullScore: 100 },
    { category: 'Test Coverage', score: 98, fullScore: 100 },
    { category: 'Security Validation', score: 96, fullScore: 100 },
    { category: 'Change Control', score: 94, fullScore: 100 }
  ];

  const codeExportFormats = [
    { name: 'Git Branch', value: 100, color: '#F1502F' },
    { name: 'Pull Request', value: 100, color: '#0366D6' },
    { name: 'Patch File', value: 95, color: '#28A745' },
    { name: 'Docker Image', value: 98, color: '#2496ED' },
    { name: 'CI/CD Pipeline', value: 96, color: '#6F42C1' },
    { name: 'Code Review', value: 100, color: '#FD7E14' }
  ];

  const evolutionTrailExample = [
    { timestamp: '2024-01-15 09:00', agent: 'artemis_scanner', action: 'Repository analyzed', details: 'Found 156 optimization opportunities' },
    { timestamp: '2024-01-15 09:15', agent: 'gpt4_optimizer', action: 'Code variant generated', details: 'Memory optimization branch created' },
    { timestamp: '2024-01-15 09:30', agent: 'claude_optimizer', action: 'Code variant generated', details: 'Performance optimization branch created' },
    { timestamp: '2024-01-15 09:45', agent: 'artemis_validator', action: 'Tests executed', details: 'All 342 tests passed on both branches' },
    { timestamp: '2024-01-15 10:00', agent: 'artemis_benchmarker', action: 'Performance measured', details: '45% faster, 30% less memory' },
    { timestamp: '2024-01-15 10:15', agent: 'artemis_evolution', action: 'Best variant selected', details: 'Performance branch wins with score: 94.2' },
    { timestamp: '2024-01-15 10:30', agent: 'human_developer', action: 'Code reviewed & approved', details: 'Merged to main branch' }
  ];

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '12px 24px',
        background: activeTab === id ? 'linear-gradient(135deg, #F4511E 0%, #FF6D00 100%)' : '#f0f0f0',
        color: activeTab === id ? 'white' : '#333',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s ease',
        boxShadow: activeTab === id ? '0 4px 15px rgba(244, 81, 30, 0.3)' : 'none'
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      {label}
    </button>
  );

  const FeatureCard = ({ title, icon, children }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}>
      <h3 style={{
        margin: '0 0 20px 0',
        fontSize: '20px',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '10px',
            background: 'linear-gradient(135deg, #F4511E 0%, #FF6D00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Artemis Governance & Compliance
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#666',
            lineHeight: '1.6'
          }}>
            Complete transparency and control over AI-driven code evolution and optimization
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '30px',
          flexWrap: 'wrap'
        }}>
          <TabButton id="overview" label="Overview" icon="🏛️" />
          <TabButton id="auditability" label="Code Auditability" icon="📋" />
          <TabButton id="traceability" label="Evolution Traceability" icon="🔍" />
          <TabButton id="interoperability" label="Integration & Export" icon="🔌" />
          <TabButton id="compliance" label="Quality & Security" icon="⚖️" />
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div>
            <FeatureCard title="Artemis Governance Overview" icon="🏛️">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {[
                  {
                    title: 'Complete Code Auditability',
                    icon: '📋',
                    description: 'Every code change, optimization decision, and evolution step is tracked and attributable',
                    metric: '100% Traceable'
                  },
                  {
                    title: 'Evolution Transparency',
                    icon: '🧬',
                    description: 'Full visibility into how code evolves from original to optimized across all AI agents',
                    metric: 'Every Generation'
                  },
                  {
                    title: 'Seamless Integration',
                    icon: '🔌',
                    description: 'Export optimized code via Git, PRs, or integrate with any CI/CD pipeline',
                    metric: '6+ Methods'
                  }
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    background: '#fff3e0',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #ffccbc'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>{feature.icon}</div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{feature.title}</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>{feature.description}</p>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#F4511E'
                    }}>{feature.metric}</div>
                  </div>
                ))}
              </div>

              <h4 style={{ marginBottom: '20px' }}>Code Quality & Compliance Score</h4>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={complianceScore}>
                  <PolarGrid stroke="#e0e0e0" />
                  <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar name="Current Score" dataKey="score" stroke="#F4511E" fill="#F4511E" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'auditability' && (
          <div>
            <FeatureCard title="Complete Code Evolution Audit Trail" icon="📋">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px' }}>What Artemis Tracks</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {[
                    { category: 'Code Analysis', items: ['Repository scans', 'Performance bottlenecks', 'Security vulnerabilities', 'Code smells'] },
                    { category: 'AI Agent Actions', items: ['Optimization suggestions', 'Code generations', 'Agent decisions', 'Score calculations'] },
                    { category: 'Evolution Process', items: ['Parent-child relationships', 'Mutation operations', 'Crossover events', 'Selection criteria'] },
                    { category: 'Validation & Testing', items: ['Test executions', 'Benchmark results', 'Security scans', 'Human reviews'] }
                  ].map((section, idx) => (
                    <div key={idx} style={{
                      background: '#fff3e0',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #ffccbc'
                    }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#F4511E' }}>{section.category}</h5>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                        {section.items.map((item, i) => (
                          <li key={i} style={{ marginBottom: '5px' }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <h4 style={{ marginBottom: '15px' }}>Live Evolution Audit Trail</h4>
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F4511E', color: 'white' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Timestamp</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Agent/User</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evolutionTrailExample.map((entry, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.timestamp}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.agent}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.action}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'traceability' && (
          <div>
            <FeatureCard title="Code Evolution Lineage & Traceability" icon="🔍">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px' }}>Evolution Flow Tracking</h4>
                <div style={{
                  background: '#fff3e0',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', overflowX: 'auto' }}>
                    {[
                      { step: 'Original Code', icon: '📄', details: 'main.py (1,234 lines)' },
                      { step: 'Analysis', icon: '🔍', details: '5 bottlenecks found' },
                      { step: 'AI Generation', icon: '🤖', details: '12 variants created' },
                      { step: 'Evolution', icon: '🧬', details: '3 generations evolved' },
                      { step: 'Validation', icon: '✅', details: 'Tests: 100% pass' },
                      { step: 'Optimized', icon: '🚀', details: '45% faster code' }
                    ].map((stage, idx) => (
                      <div key={idx} style={{
                        minWidth: '150px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: '#F4511E',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          margin: '0 auto 10px'
                        }}>
                          {stage.icon}
                        </div>
                        <h5 style={{ margin: '0 0 5px 0' }}>{stage.step}</h5>
                        <p style={{ fontSize: '12px', color: '#666' }}>{stage.details}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <h4 style={{ marginBottom: '15px' }}>Code Variant Genealogy</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  <div style={{
                    background: '#fff3e0',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #ffccbc'
                  }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#F4511E' }}>🧬 Evolution Provenance</h5>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Generation: 3</li>
                      <li>Parent Variants: opt_v2.1, perf_v2.3</li>
                      <li>Mutation Type: Crossover + Local Search</li>
                      <li>Fitness Score: 94.2/100</li>
                      <li>AI Agents Used: GPT-4, Claude-3, Gemini</li>
                      <li>Evolution Time: 45 minutes</li>
                    </ul>
                  </div>
                  <div style={{
                    background: '#fff3e0',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #ffccbc'
                  }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#F4511E' }}>📊 Performance Lineage</h5>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Original Performance: 2.3s</li>
                      <li>Gen 1 Best: 1.8s (-22%)</li>
                      <li>Gen 2 Best: 1.4s (-39%)</li>
                      <li>Gen 3 Best: 1.26s (-45%)</li>
                      <li>Memory: 120MB → 84MB</li>
                      <li>CPU Usage: 85% → 62%</li>
                    </ul>
                  </div>
                </div>

                <h4 style={{ marginBottom: '15px', marginTop: '30px' }}>Multi-Objective Evolution Path</h4>
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                    Artemis tracks how each code variant balances multiple objectives through evolution:
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '10px',
                    textAlign: 'center'
                  }}>
                    {['Performance', 'Memory', 'Readability', 'Security'].map((objective, idx) => (
                      <div key={idx}>
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#F4511E',
                          marginBottom: '5px'
                        }}>
                          {['+45%', '-30%', '92/100', '98/100'][idx]}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{objective}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'interoperability' && (
          <div>
            <FeatureCard title="Seamless Code Integration & Export" icon="🔌">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '20px' }}>Integration Methods</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={codeExportFormats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#F4511E">
                      {codeExportFormats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                <div style={{
                  background: '#fff3e0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#F1502F' }}>🌿 Git Integration</h5>
                  <pre style={{
                    background: '#2d2d2d',
                    color: '#f8f8f2',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
{`# Artemis creates feature branches
git checkout -b artemis/perf-optimization

# Commits with detailed messages
git commit -m "perf: optimize data processing
- Reduced complexity from O(n²) to O(n log n)
- Added parallel processing
- Memory usage reduced by 30%
Generated by Artemis v2.1"`}
                  </pre>
                </div>

                <div style={{
                  background: '#fff3e0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#0366D6' }}>🔄 Pull Request</h5>
                  <pre style={{
                    background: '#2d2d2d',
                    color: '#f8f8f2',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
{`# Auto-generated PR description
## 🚀 Performance Optimization

### Changes
- Optimized main processing loop
- Added caching layer
- Refactored database queries

### Results
- 45% faster execution
- 30% less memory usage
- All tests passing ✅

### AI Agents Used
- GPT-4: Algorithm optimization
- Claude-3: Code refactoring`}
                  </pre>
                </div>

                <div style={{
                  background: '#fff3e0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#6F42C1' }}>🔧 CI/CD Pipeline</h5>
                  <pre style={{
                    background: '#2d2d2d',
                    color: '#f8f8f2',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
{`# .github/workflows/artemis.yml
name: Artemis Optimization
on:
  schedule:
    - cron: '0 2 * * 1' # Weekly
jobs:
  optimize:
    runs-on: ubuntu-latest
    steps:
      - uses: turintech/artemis-action@v2
        with:
          targets: 'src/'
          objectives: 'performance,security'
          auto-merge: true`}
                  </pre>
                </div>

                <div style={{
                  background: '#fff3e0',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#FD7E14' }}>📝 Code Review Format</h5>
                  <pre style={{
                    background: '#2d2d2d',
                    color: '#f8f8f2',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
{`# Artemis Review Report
## Executive Summary
- Files analyzed: 23
- Optimizations found: 12
- Estimated impact: 40% faster

## Detailed Changes
1. main.py:45 - Replaced nested loops
   Before: O(n³) | After: O(n log n)
   
2. utils.py:123 - Added memoization
   Memory trade-off: +5MB for 10x speed

## Risk Assessment
- Breaking changes: None
- Test coverage: 100%
- Security impact: Improved`}
                  </pre>
                </div>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div>
            <FeatureCard title="Code Quality & Security Compliance" icon="⚖️">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {[
                  {
                    standard: 'Code Quality Standards',
                    icon: '✨',
                    features: ['PEP 8 / ESLint compliance', 'Cyclomatic complexity limits', 'Test coverage requirements', 'Documentation standards'],
                    status: 'Enforced'
                  },
                  {
                    standard: 'Security Standards',
                    icon: '🔒',
                    features: ['OWASP compliance', 'Dependency scanning', 'Secret detection', 'Vulnerability assessment'],
                    status: 'Automated'
                  },
                  {
                    standard: 'Performance Standards',
                    icon: '⚡',
                    features: ['Benchmark validation', 'Resource limits', 'Latency requirements', 'Scalability testing'],
                    status: 'Verified'
                  },
                  {
                    standard: 'Evolution Controls',
                    icon: '🧬',
                    features: ['Human approval required', 'Gradual rollout support', 'Rollback capability', 'A/B testing ready'],
                    status: 'Built-in'
                  }
                ].map((compliance, idx) => (
                  <div key={idx} style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '15px'
                    }}>
                      <span style={{ fontSize: '32px', marginRight: '10px' }}>{compliance.icon}</span>
                      <div>
                        <h4 style={{ margin: 0 }}>{compliance.standard}</h4>
                        <span style={{
                          background: '#4CAF50',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>{compliance.status}</span>
                      </div>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                      {compliance.features.map((feature, i) => (
                        <li key={i} style={{ marginBottom: '5px' }}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <h4 style={{ marginBottom: '15px' }}>Artemis Safety Features</h4>
              <div style={{
                background: '#fff3e0',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {[
                    { feature: 'Test-Driven Evolution', description: 'No code change without passing all existing tests' },
                    { feature: 'Incremental Changes', description: 'Small, reviewable changes instead of massive rewrites' },
                    { feature: 'Semantic Preservation', description: 'Guarantees functional equivalence unless specified' },
                    { feature: 'Performance Regression Detection', description: 'Alerts if optimizations degrade other metrics' },
                    { feature: 'Human-in-the-Loop', description: 'Critical changes require human approval' },
                    { feature: 'Explainable Optimizations', description: 'Every change comes with clear reasoning' }
                  ].map((item, idx) => (
                    <div key={idx}>
                      <h5 style={{ margin: '0 0 5px 0', color: '#F4511E' }}>{item.feature}</h5>
                      <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <h4 style={{ marginBottom: '15px', marginTop: '30px' }}>Validation Pipeline</h4>
              <div style={{
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                  {[
                    { stage: '1. Static Analysis', status: '✅ Pass', details: 'No code smells' },
                    { stage: '2. Unit Tests', status: '✅ 342/342', details: '100% coverage' },
                    { stage: '3. Integration Tests', status: '✅ 56/56', details: 'All passing' },
                    { stage: '4. Performance Tests', status: '✅ +45%', details: 'Improved' },
                    { stage: '5. Security Scan', status: '✅ Clean', details: 'No vulnerabilities' }
                  ].map((step, idx) => (
                    <div key={idx} style={{
                      textAlign: 'center',
                      padding: '10px'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>{step.stage}</div>
                      <div style={{ fontSize: '16px', color: '#4CAF50', marginBottom: '3px' }}>{step.status}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{step.details}</div>
                    </div>
                  ))}
                </div>
              </div>
            </FeatureCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtemisGovernance; 